'use client';
import Badge from '@/components/Badge';
import { ButtonProps } from '@/components/Button/Button';
import CheckCircle from '@/components/icons/CheckCircle';
import CrossCircle from '@/components/icons/CrossCircle';
import PlusCircle from '@/components/icons/PlusCircle';
import { Modal } from '@/components/Modal/Modal';
import Table from '@/components/Table';
import { PartialRowData } from '@/components/Table/Table';
import {
  useDeleteEmpMutation,
  useLazyGetListAdminsQuery,
} from '@/services/API';
import { useAppSelector } from '@/store/hooks';
import { AccountStatus, IEmployee } from '@/types';
import env from '@/utils/env';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type AdminTableData = Pick<
  IEmployee,
  'id' | 'sn' | 'given_name' | 'email_address' | 'mobile' | 'account_status'
> &
  PartialRowData;

export default function Page() {
  const [tableData, setTableData] = useState<AdminTableData[]>([]);
  const selectedSte = useAppSelector((state) => state.selectedSte.societe);
  const [openedDeleteModal, setOpenedDeleteModal] = useState(false);
  const [selectedAdmins, setSelectedAdmins] = useState<AdminTableData[]>([]);
  const [deleteEmployee, { isLoading: isLoadingDelete }] =
    useDeleteEmpMutation();
  const [getListAdmins, { data: admins, isLoading: isLoadingGetList }] =
    useLazyGetListAdminsQuery();
  const navigate = useNavigate();

  const edit = (row: AdminTableData) => {
    navigate('modifier/?id=' + row.id);
  };
  const profil = env().PROFIL;

  function renderIcon(value?: AccountStatus) {
    switch (value) {
      case AccountStatus.ACTIVE:
        return <Badge variant="success" icon={<CheckCircle />} label="Actif" />;
      case AccountStatus.DISABLED_PARTNER:
        return (
          <Badge variant="disabled" icon={<CrossCircle />} label="Désactivé" />
        );

      case AccountStatus.DISABLED_BNPPF:
        return <Badge variant="danger" icon={<CrossCircle />} label="Bloqué" />;

      default:
        return '-';
    }
  }

  useEffect(() => {
    if (admins) {
      setTableData(
        admins.map<AdminTableData>(
          ({ sn, id, given_name, email_address, mobile, account_status }) => ({
            sn,
            id,
            given_name,
            email_address,
            mobile,
            account_status,
            disable:
              (profil === 'PARTENAIRE' &&
                account_status === AccountStatus.DISABLED_BNPPF) ||
              (profil === 'PF' &&
                account_status === AccountStatus.DISABLED_PARTNER)
                ? true
                : false,
          })
        )
      );
    }
  }, [admins]);

  useEffect(() => {
    getListAdmins(selectedSte.siren);
  }, []);

  function deleteAdmin() {
    let req = {
      siren: selectedSte.siren,
      resources: selectedAdmins.map(({ id }) => ({
        id,
      })),
    };
    deleteEmployee(req)
      .unwrap()
      .then(() => {
        setOpenedDeleteModal(false);
        getListAdmins(selectedSte.siren);
      });
  }

  const columnHelper = createColumnHelper<AdminTableData>();
  const cols = [
    columnHelper.accessor('sn', {
      header: () => 'Nom',
      meta: { filterVariant: 'text' },
    }),
    columnHelper.accessor('given_name', {
      header: () => 'Prénom',
      meta: { filterVariant: 'text' },
    }),
    columnHelper.accessor('email_address', {
      header: () => 'Adresse e-mail',
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor('account_status', {
      header: () => 'Statut',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ getValue }) => renderIcon(getValue()),
    }),
  ] as ColumnDef<AdminTableData>[];

  const leftbtns: ButtonProps[] = [
    {
      variant: 'secondary',
      children: 'Importer',
      onClick: () => navigate('importer'),
    },
  ];

  const rightbtns: ButtonProps[] = [
    {
      disabled: selectedAdmins.length === 0,
      onClick: () => setOpenedDeleteModal(true),
      variant: 'secondary',
      children: 'Supprimer',
    },
    {
      onClick: () => navigate('creer'),
      icon: <PlusCircle />,
      children: 'Ajouter',
    },
  ];

  return (
    <>
      {/* @ts-ignore */}
      <Table<AdminTableData>
        title="Liste des administrateurs"
        leftbtns={leftbtns}
        rightbtns={rightbtns}
        isLoadingData={isLoadingGetList}
        columnsSort={[{ id: 'sn', desc: false }]}
        onEdit={edit}
        columns={cols}
        data={tableData}
        enableFilter={true}
        onSelectionChange={setSelectedAdmins}
      />
      <Modal
        primaryBtnProps={{
          loading: isLoadingDelete,
          children: 'Confirmer',
          onClick: deleteAdmin,
        }}
        secondaryBtnProps={{
          children: 'Annuler',
          onClick: () => setOpenedDeleteModal(false),
        }}
        title="Voulez-vous supprimer l'admin ?"
        open={openedDeleteModal}
        onClose={() => setOpenedDeleteModal(false)}
      />
    </>
  );
}
